import { useState } from 'react'
import {
  MoreVertical,
  FileText,
  FileCheck,
  Trash2,
  Store,
  RefreshCw,
  Link,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SelectionBar } from '@/components/common/SelectionBar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { toast } from '@/stores/useToastStore'
import { BulkActionModal } from './BulkActionModal'
import { QuoteSheetModal } from './QuoteSheetModal'
import { CommitmentLetterWizard } from './CommitmentLetterWizard/CommitmentLetterWizard'
import { MoveToPlanModal } from './MoveToPlanModal'
import { useBulkDeleteLines } from '@/hooks/usePlanDetail'
import type { PlanDetailItem } from '@/types'

interface BulkActionsBarProps {
  planId: string
  versionId?: string
  selectedCount: number
  selectedItems: PlanDetailItem[]
  onClearSelection: () => void
  onDeleteSuccess?: () => void
  onAssignStoreGroup: () => void
  onManageStoreGroups?: () => void
  canManageStoreGroups?: boolean
  isReadOnly?: boolean
  onReplaceDummySkus?: () => void
  hasDummySkusSelected?: boolean
  onAssignParentSku?: () => void
  hasChildSkusSelected?: boolean
}

export function BulkActionsBar({
  planId,
  versionId,
  selectedCount,
  selectedItems,
  onClearSelection,
  onDeleteSuccess,
  onAssignStoreGroup,
  onManageStoreGroups,
  canManageStoreGroups,
  isReadOnly,
  onReplaceDummySkus,
  hasDummySkusSelected,
  onAssignParentSku,
  hasChildSkusSelected,
}: BulkActionsBarProps) {
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [quoteSheetOpen, setQuoteSheetOpen] = useState(false)
  const [commitmentLetterOpen, setCommitmentLetterOpen] = useState(false)
  const [moveToPlanOpen, setMoveToPlanOpen] = useState(false)

  const bulkDeleteMutation = useBulkDeleteLines(planId)

  const handleAssignStoreGroup = () => {
    onAssignStoreGroup()
  }

  const handleCreateQuoteSheet = () => {
    setQuoteSheetOpen(true)
  }

  const handleCreateCommitmentLetter = () => {
    setCommitmentLetterOpen(true)
  }

  const handleDeleteLines = () => {
    setBulkDeleteOpen(true)
  }

  const handleConfirmBulkDelete = () => {
    bulkDeleteMutation.mutate(
      selectedItems.map(item => item.id),
      {
        onSuccess: () => {
          toast.success('The lines were successfully deleted')
          setBulkDeleteOpen(false)
          onClearSelection()
          onDeleteSuccess?.()
        },
        onError: (error: Error) => {
          toast.error(`Failed to delete lines: ${error.message}`)
        },
      }
    )
  }

  return (
    <>
      <SelectionBar
        count={selectedCount}
        noun="Item"
        onClear={onClearSelection}
      >
        {/* Action buttons - NO ICONS except More Actions */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleAssignStoreGroup}
          className="border-primary text-primary hover:bg-primary/10"
          disabled={isReadOnly}
        >
          Assign Store Model
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setMoveToPlanOpen(true)}
          className="border-primary text-primary hover:bg-primary/10"
          disabled={isReadOnly}
        >
          Move to Plan
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-primary text-primary hover:bg-primary/10"
            >
              <MoreVertical className="h-4 w-4" />
              More Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuItem
                    onClick={onManageStoreGroups}
                    disabled={isReadOnly || !canManageStoreGroups}
                  >
                    <Store className="h-4 w-4 mr-2" />
                    Manage Store Groups
                  </DropdownMenuItem>
                </TooltipTrigger>
                {!canManageStoreGroups && (
                  <TooltipContent side="left">
                    All selected lines must share the same store model
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            {onAssignParentSku && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      onClick={onAssignParentSku}
                      disabled={isReadOnly || !hasChildSkusSelected}
                    >
                      <Link className="h-4 w-4 mr-2" />
                      Assign Parent SKU
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  {!hasChildSkusSelected && (
                    <TooltipContent side="left">
                      Select at least one Child SKU to assign a parent
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <DropdownMenuItem
              onClick={onReplaceDummySkus}
              disabled={isReadOnly || !hasDummySkusSelected}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Replace Dummy SKUs
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleCreateQuoteSheet}>
              <FileText className="h-4 w-4 mr-2" />
              Create Quote Sheet
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCreateCommitmentLetter}>
              <FileCheck className="h-4 w-4 mr-2" />
              Create Commitment Letter
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleDeleteLines}
              className="text-destructive focus:text-destructive"
              disabled={isReadOnly}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Lines
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SelectionBar>

      <BulkActionModal
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        selectedItems={selectedItems}
        onConfirm={handleConfirmBulkDelete}
        isLoading={bulkDeleteMutation.isPending}
        title="Delete Lines"
        description="This action will permanently remove these lines from the assortment plan."
        confirmText="Delete"
        loadingText="Deleting..."
        confirmVariant="destructive"
      />

      <QuoteSheetModal
        open={quoteSheetOpen}
        onOpenChange={setQuoteSheetOpen}
        planId={planId}
        versionId={versionId}
        selectedItems={selectedItems}
        onClearSelection={onClearSelection}
      />

      <CommitmentLetterWizard
        open={commitmentLetterOpen}
        onOpenChange={setCommitmentLetterOpen}
        planId={planId}
        versionId={versionId}
        selectedItems={selectedItems}
        onClearSelection={onClearSelection}
      />

      {versionId && (
        <MoveToPlanModal
          open={moveToPlanOpen}
          onOpenChange={setMoveToPlanOpen}
          planId={planId}
          versionId={versionId}
          selectedItems={selectedItems}
          onSuccess={() => {
            onClearSelection()
            onDeleteSuccess?.()
          }}
        />
      )}
    </>
  )
}
